import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { toast } from "sonner";

const ERROR_MESSAGES = {
  google_denied: "Google sign-in was cancelled.",
  invalid_state: "Sign-in session expired — please try again.",
  token_exchange_failed: "Couldn't verify your Google account — please try again.",
  userinfo_failed: "Couldn't verify your Google account — please try again.",
  email_not_verified: "Your Google email isn't verified. Use a verified Google account.",
  domain_not_allowed: "Only @medpackindia.com accounts can sign in.",
  no_account: "No ERP account is set up for your email. Contact your admin to get access.",
  google_not_configured: "Google sign-in isn't set up on this server yet — use password sign-in below.",
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  // TEMPORARY fallback: Google Sign-In needs medpack.yantravision.com live over
  // HTTPS (Google rejects bare IPs / plain HTTP), which isn't ready yet.
  // Password login stays available — hidden behind a toggle — purely so the
  // rest of the ERP can be tested over the plain IP in the meantime. Remove
  // this block (and the showPassword state/toggle) once the domain + TLS
  // are live and Google Sign-In has been verified end-to-end.
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error) {
      toast.error(ERROR_MESSAGES[error] || "Sign-in failed. Please try again.");
      window.history.replaceState(null, "", "/login");
    }
  }, []);

  const signInWithGoogle = () => {
    setLoading(true);
    window.location.href = "/api/auth/google/login";
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    const res = await login(email, password);
    setPwLoading(false);
    if (res.ok) {
      toast.success("Signed in");
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:block bg-neutral-950">
        <img
          src="https://images.pexels.com/photos/19837529/pexels-photo-19837529.jpeg"
          alt="Industrial printing press"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF5A00] flex items-center justify-center">
              <Printer size={20} className="text-white" />
            </div>
            <span className="font-display font-black tracking-tight">MEDPACK ERP</span>
          </div>
          <div>
            <div className="label-caps text-white/60 mb-3">Operational Command</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
              Every job.<br/>Every operator.<br/>Every ₹.
            </h2>
            <p className="mt-6 text-sm text-white/70 max-w-md leading-relaxed">
              From order intake through dispatch — track production, control inventory, and know
              the profit on every roll you ship.
            </p>
          </div>
          <div className="label-caps text-white/40 text-[10px]">
            v1.0 · Internal Use Only
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-[#F7F7F8] grain-bg">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="label-caps mb-2">Sign in</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">Access the ERP</h1>
            <p className="mt-2 text-sm text-neutral-600">
              Sign in with your company Google account.
            </p>
          </div>

          <button
            type="button"
            data-testid="google-signin"
            onClick={signInWithGoogle}
            disabled={loading}
            className="w-full h-11 rounded-sm border border-neutral-300 bg-white flex items-center justify-center gap-3 font-medium text-neutral-800 hover:bg-neutral-50 transition-colors disabled:opacity-60"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.98v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.98A9 9 0 0 0 0 9c0 1.45.35 2.83.98 4.03l2.97-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .98 4.97l2.97 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
            </svg>
            {loading ? "Redirecting…" : "Sign in with Google"}
          </button>

          {/* TEMPORARY password fallback — remove once Google Sign-In is live on the real domain */}
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="text-xs text-neutral-500 underline underline-offset-2 hover:text-neutral-700"
            >
              {showPassword ? "Hide password sign-in" : "Testing over the IP? Use password sign-in"}
            </button>
            {showPassword && (
              <form onSubmit={onPasswordSubmit} className="space-y-3 mt-3" data-testid="login-form">
                <div>
                  <label className="label-caps block mb-1.5">Email</label>
                  <Input
                    data-testid="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Password</label>
                  <Input
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="rounded-sm"
                  />
                </div>
                <Button
                  data-testid="login-submit"
                  type="submit"
                  disabled={pwLoading}
                  className="w-full rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] text-white h-11 font-medium tracking-wide"
                >
                  {pwLoading ? "Signing in…" : "Sign in →"}
                </Button>
              </form>
            )}
          </div>

          <div className="mt-8 text-xs text-neutral-500 border-t border-neutral-200 pt-4">
            Sign in with your @medpackindia.com Google account.
          </div>
        </div>
      </div>
    </div>
  );
}
